import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  ApolloClient,
  ApolloLink,
  DocumentNode,
  NormalizedCacheObject,
  OperationVariables,
  SubscriptionHookOptions,
  useSubscription as useBaseSubscription,
} from '@apollo/client';
import { captureException } from '@/utils/sentry';
import { createClient } from 'graphql-ws';
import {
  generateSentryReporter,
  NOT_AUTHORIZED_EXCEPTION_CODE,
  sharedCache,
} from '@/utils/apollo-client';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { HeaderBuilder } from '@platform/http/blocs/services/header-builder';
import { SessionService } from '@domain/auth/blocs/services/session-service';
import { shishitoConfig } from '@/utils/environment';

export const PING_TIMEOUT_MS = 10000;

export interface SubscriptionContextType {
  apolloClient: ApolloClient<NormalizedCacheObject>;
  isConnected: boolean;
}

export const SubscriptionContext =
  createContext<SubscriptionContextType | null>(null);

interface Props {
  children?: React.ReactNode;
}

export const SubscriptionContextProvider: React.FC<Props> = ({ children }) => {
  const pingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [authToken, setAuthToken] = useState<string>();
  const [isConnected, setIsConnected] = useState(true);

  const sentryReporter = useMemo(() => {
    return generateSentryReporter(async () => {
      try {
        // Force a refresh here. getIdToken could return the same stale token
        // (clock skew), and then the reconnect effect below would never fire
        setAuthToken(await SessionService.refreshIdTokenOnce());
      } catch (e) {
        // @ts-ignore code exists
        if (e.code !== NOT_AUTHORIZED_EXCEPTION_CODE) {
          captureException(e);
        }
      }
    });
  }, []);

  const wsClient = useMemo(() => {
    return createClient({
      connectionParams: async () => {
        return {
          headers: await HeaderBuilder.create().withAuth().build(),
        };
      },
      on: {
        ping: (received) => {
          // received is true when the ping is received from the server, but
          // false when the ping is sent from the client
          if (received) {
            setIsConnected(true);

            if (pingTimeoutRef.current) {
              clearTimeout(pingTimeoutRef.current);
            }

            // Set isConnected to false if another ping is not received from
            // the server within the expected timeout
            pingTimeoutRef.current = setTimeout(() => {
              setIsConnected(false);
            }, PING_TIMEOUT_MS);
          }
        },
      },
      retryAttempts: Infinity,
      // By default, graphql-ws will retry with exponential backoff
      // https://the-guild.dev/graphql/ws/recipes#client-usage-with-retry-on-any-connection-problem
      shouldRetry: () => true,
      url: shishitoConfig.graphQLWebSocketURL,
    });
  }, []);

  // Temporarily terminates ws connection when authToken changes and client
  // will automatically attempt to reconnect
  useEffect(() => {
    wsClient.terminate();
  }, [authToken, wsClient]);

  const value = useMemo<SubscriptionContextType>(() => {
    return {
      apolloClient: new ApolloClient({
        cache: sharedCache,
        connectToDevTools: process.env.NODE_ENV !== 'test',
        link: ApolloLink.from([sentryReporter, new GraphQLWsLink(wsClient)]),
      }),
      isConnected,
    };
  }, [isConnected, sentryReporter, wsClient]);

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscriptionContext = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription requires a SubscriptionContextProvider');
  }

  return context;
};

export function useSubscription<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Q = any,
  V extends OperationVariables = OperationVariables,
>(subscription: DocumentNode, defaultOptions?: SubscriptionHookOptions<Q, V>) {
  const context = useSubscriptionContext();
  const options = useMemo(
    () => ({
      client: context.apolloClient,
      ...(defaultOptions || {}),
    }),
    [context.apolloClient, defaultOptions],
  );

  return useBaseSubscription<Q, V>(subscription, options);
}
